import { Link } from "react-router-dom";
import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

export interface ServiceItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  image: string;
}

export interface ServicesGridContent {
  badge?: string;
  headline: string;
  headlineAccent?: string;
  subtext: string;
  services: ServiceItem[];
  ctaLabel: string;
  ctaPath: string;
}

interface ServicesGridProps {
  content: ServicesGridContent;
}

export default function ServicesGrid({ content }: ServicesGridProps) {
  const { badge, headline, headlineAccent, subtext, services, ctaLabel, ctaPath } = content;
  const headerRef = useScrollRevealChildren();
  const gridRef = useScrollRevealChildren();

  const primary = services[0];
  const secondary = services[1];
  const tertiary = services[2];
  const rest = services.slice(3);

  return (
    <section className="bg-white py-24 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-6 xl:px-10">

        {/* Header */}
        <div ref={headerRef} className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-8">
          <div className="max-w-2xl">
            {badge && (
              <div className="reveal flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-[#C9A961]" />
                <span
                  className="text-[#C9A961] text-[11px] tracking-[0.25em] uppercase font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {badge}
                </span>
              </div>
            )}
            <h2
              className="reveal delay-100 text-4xl md:text-5xl xl:text-[52px] text-[#1C1C1C] leading-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
            >
              {headline}
              {headlineAccent && (
                <em
                  className="block text-[#C9A961]"
                  style={{ fontStyle: "italic" }}
                >
                  {headlineAccent}
                </em>
              )}
            </h2>
            <p
              className="reveal delay-200 text-[#777] text-base leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
            >
              {subtext}
            </p>
          </div>
          <Link
            to={ctaPath}
            className="reveal delay-300 bg-[#1C1C1C] text-white text-[11px] tracking-[0.18em] uppercase font-semibold px-8 py-4 hover:bg-[#C9A961] transition-all duration-300 cursor-pointer whitespace-nowrap self-start md:self-end"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {ctaLabel}
          </Link>
        </div>

        {/* Editorial grid */}
        <div ref={gridRef} className="space-y-px">

          {/* Row 1: Large primary + stacked secondary/tertiary */}
          {primary && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#F0EBE2]">
              {/* Large featured service */}
              <Link
                to={ctaPath}
                className="reveal-left lg:col-span-2 group relative overflow-hidden h-[480px] cursor-pointer block"
              >
                <img
                  src={primary.image}
                  alt={primary.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-px w-8 bg-[#C9A961]" />
                    <span
                      className="text-[#C9A961] text-[10px] tracking-[0.2em] uppercase"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Signature Service
                    </span>
                  </div>
                  <h3
                    className="text-white text-3xl md:text-4xl font-bold mb-3 group-hover:text-[#C9A961] transition-colors duration-300"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {primary.title}
                  </h3>
                  <p
                    className="text-white/60 text-sm leading-relaxed max-w-md mb-5"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                  >
                    {primary.description}
                  </p>
                  <div className="flex items-center gap-2 text-[#C9A961]">
                    <span className="text-[11px] tracking-[0.16em] uppercase font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Explore Service
                    </span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Stacked right column */}
              <div className="flex flex-col gap-px">
                {[secondary, tertiary].filter(Boolean).map((service, idx) => service && (
                  <Link
                    key={service.id}
                    to={ctaPath}
                    className={`reveal-right delay-${(idx + 1) * 100} group relative overflow-hidden flex-1 cursor-pointer block min-h-[236px]`}
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-1000"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                      <h3
                        className="text-white text-xl font-bold mb-1 group-hover:text-[#C9A961] transition-colors duration-300"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {service.title}
                      </h3>
                      <p
                        className="text-white/55 text-xs leading-relaxed"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {service.description.substring(0, 80)}...
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Row 2: Remaining services — horizontal editorial strip */}
          {rest.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#F0EBE2]">
              {rest.map((service, idx) => (
                <Link
                  key={service.id}
                  to={ctaPath}
                  className={`reveal delay-${idx * 100 + 100} group relative overflow-hidden h-72 cursor-pointer block`}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-1000"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-7">
                    <h3
                      className="text-white text-2xl font-bold mb-2 group-hover:text-[#C9A961] transition-colors duration-300"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {service.title}
                    </h3>
                    <p
                      className="text-white/55 text-xs leading-relaxed"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {service.description.substring(0, 75)}...
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-[#F0EBE2] pt-10">
          <div>
            <p
              className="text-[#1C1C1C] text-sm font-semibold mb-0.5"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Complete kitchen packages starting at <span className="text-[#C9A961]">$9,995</span>
            </p>
            <p className="text-[#AAA] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              Serving Boca Raton, Delray Beach &amp; all of South Florida
            </p>
          </div>
          <Link
            to="/locations"
            className="flex items-center gap-3 bg-[#C9A961] text-white text-[11px] tracking-[0.18em] uppercase font-semibold px-10 py-4 hover:bg-[#b8964f] transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Request a Free Estimate
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
